import { ChartContainer, ChartTooltip, ChartTooltipContent } from 'components/ui/chart';
import { format as formatDate, parseISO } from 'date-fns';
import { Area, AreaChart, CartesianGrid, XAxis } from 'recharts';
import type { FigurePoint } from 'types/homeWidgetFigures';
import type { HomeWidgetPeriod } from 'types/homeWidgets';
import { ChangeChip, Headline, KpiFormProps, SIDE_CLASS, TargetStrip } from './KpiParts';

/**
 * How a point is named under the chart, and at the chart's two ends where only those are named. A week and a month
 * are drawn by the day, the two longer periods by the month.
 */
const POINT_LABEL: Partial<Record<HomeWidgetPeriod, { each: string; end: string }>> = {
    THIS_WEEK: { each: 'EEE', end: 'EEE d MMM' },
    THIS_MONTH: { each: 'd', end: 'd MMM' },
};
const MONTH_LABEL = { each: 'MMM', end: 'MMM yyyy' };

function pointLabel(point: FigurePoint, pattern: string) {
    return formatDate(parseISO(point.from), pattern);
}

/**
 * The figure over time as an area. Two columns wide, the figure stands beside the chart, which gains grid lines
 * and a label per point; one column wide it stands above, and only the first and the last point are named.
 */
export function TrendForm({ name, definition, figure, size, links, format }: KpiFormProps) {
    const wide = size === '2x2';
    const points = figure.points ?? [];
    const labels = POINT_LABEL[definition.period] ?? MONTH_LABEL;
    const data = points.map((point) => ({ label: pointLabel(point, labels.each), value: point.value }));

    const headline = <Headline value={figure.value} format={format} to={links?.list} />;
    const change = <ChangeChip figure={figure} period={definition.period} />;
    const target = definition.target ? (
        <TargetStrip figure={figure} target={definition.target} format={format} className={wide ? 'mt-2' : undefined} />
    ) : null;

    const chart = (
        <ChartContainer config={{}} role="img" aria-label={name} className="aspect-auto min-h-0 w-full flex-1">
            <AreaChart data={data} accessibilityLayer={false} margin={{ top: 4, right: 0, bottom: 0, left: 0 }}>
                {wide ? <CartesianGrid vertical={false} stroke="var(--color-divider-soft)" /> : null}
                {/* Inset when shown, so the first and the last label are not cut at the chart's edges. */}
                <XAxis
                    dataKey="label"
                    hide={!wide}
                    tickLine={false}
                    axisLine={false}
                    interval="preserveStartEnd"
                    padding={wide ? { left: 14, right: 14 } : undefined}
                    fontSize="0.75rem"
                />
                <ChartTooltip
                    content={
                        <ChartTooltipContent
                            hideIndicator
                            formatter={(value) => (
                                <span className="text-foreground font-medium tabular-nums">
                                    {format(Number(value))}
                                </span>
                            )}
                        />
                    }
                />
                <Area
                    dataKey="value"
                    type="linear"
                    stroke="var(--color-primary)"
                    strokeWidth={2}
                    fill="var(--color-primary-tint)"
                    fillOpacity={1}
                    isAnimationActive={false}
                />
            </AreaChart>
        </ChartContainer>
    );

    if (wide) {
        return (
            <div className="flex min-h-0 flex-1 gap-6">
                <div className={SIDE_CLASS}>
                    {headline}
                    <div className="flex min-w-0 items-center gap-2">{change}</div>
                    {target}
                </div>
                <div className="flex min-w-0 flex-1 flex-col">{chart}</div>
            </div>
        );
    }
    return (
        <>
            <div className="flex min-w-0 flex-wrap items-center gap-x-2">
                {headline}
                {change}
            </div>
            {target}
            <div className="flex min-h-0 flex-1 flex-col gap-1">
                {chart}
                {points.length ? (
                    <div aria-hidden className="text-muted-foreground text-caption flex justify-between leading-[1.17]">
                        <span>{pointLabel(points[0], labels.end)}</span>
                        <span>{pointLabel(points[points.length - 1], labels.end)}</span>
                    </div>
                ) : null}
            </div>
        </>
    );
}
